import {
  CancelSurveyReasonCode,
  CancelSurveySubscriptionType,
  WebActionEvents,
} from '@griply/types';
import { getSubscriptionSurveyContext } from '@griply/utils';
import { useCancellableSubscriptionCard } from 'features/user';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
// eslint-disable-next-line max-len -- long query hook name
import { useStripeSubscriptionManagementUrlQuery } from 'shared/hooks/queries/use-stripe-subscription-management-url-query';
import {
  SurveyAnswers,
  SurveyDetailSelection,
  useSurveySteps,
} from 'shared/hooks/use-survey-steps';
import { useTrackEvents } from 'shared/hooks/use-track-events';

export type UseCancelSubscriptionSurveyOptions = {
  onClose: () => void;
};

export const useCancelSubscriptionSurvey = ({
  onClose,
}: UseCancelSubscriptionSurveyOptions) => {
  const { t } = useTranslation();
  const track = useTrackEvents();
  const { card, variant: variantOrNull } = useCancellableSubscriptionCard();
  const variant = variantOrNull ?? CancelSurveySubscriptionType.Paid;
  const context = getSubscriptionSurveyContext(variant);
  const { refetch } = useStripeSubscriptionManagementUrlQuery(
    card?.stripeCustomerId,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onStarted = useCallback(
    () =>
      track(WebActionEvents.CancelSurveyStarted, {
        subscription_type: variant,
      }),
    [track, variant],
  );

  const onReasonSelected = useCallback(
    (reason: CancelSurveyReasonCode) =>
      track(WebActionEvents.CancelSurveyReasonSelected, {
        subscription_type: variant,
        reason_code: reason,
      }),
    [track, variant],
  );

  const onDetailSelected = useCallback(
    (selection: SurveyDetailSelection) =>
      track(WebActionEvents.CancelSurveyDetailSelected, {
        subscription_type: variant,
        ...selection,
      }),
    [track, variant],
  );

  const onSubmit = useCallback(
    async (answers: SurveyAnswers) => {
      setIsSubmitting(true);
      track(WebActionEvents.CancelSurveyCompleted, {
        subscription_type: variant,
        ...answers,
      });
      // Stripe subs manage cancellation in the browser portal; an App Store sub
      // opened on web falls back to its managementUrl (the App Store page).
      if (card?.stripeCustomerId) {
        const result = await refetch();
        if (result.data) {
          window.open(result.data, '_blank');
        }
        onClose();
        return;
      }
      if (card?.managementUrl) {
        window.open(card.managementUrl, '_blank');
      }
      onClose();
    },
    [card, onClose, refetch, track, variant],
  );

  const survey = useSurveySteps({
    context,
    submitLabel: t('cancel-subscription-survey.open.continue'),
    isSubmitting,
    onStarted,
    onReasonSelected,
    onDetailSelected,
    onSubmit,
    onExit: onClose,
  });

  return { ...survey, onCancel: onClose };
};
